import { useEffect, useRef, useState } from "react";
import { Download, Save, Plus, Trash2, Edit2, Upload, GripVertical, FileText, RefreshCw, Eye, Link as LinkIcon, Copy, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";
import { api, API_URL } from "@/lib/api";
import Modal from "@/components/site/Modal";

const EMPTY_SHOW = { id: null, title: "", tagline: "", synopsis: "", extended_synopsis: "", poster: "", link: "", trailer_url: "", genre: "", runtime: "", director: "", year: "", starring: "" };

const FIELD_GROUPS = [
  {
    eyebrow: "Cover",
    fields: [
      { key: "cover_title", label: "Title", placeholder: "PULSE MEDIA" },
      { key: "cover_subtitle", label: "Subtitle", placeholder: "Music. Film. Technology." },
      { key: "cover_year", label: "Year tag", placeholder: "2026" },
    ],
  },
  {
    eyebrow: "Company",
    fields: [
      { key: "company_intro", label: "Company intro paragraph", textarea: true, rows: 4 },
      { key: "music_pillar", label: "Music pillar", textarea: true, rows: 2 },
      { key: "film_pillar", label: "Film pillar", textarea: true, rows: 2 },
      { key: "tech_pillar", label: "Technology pillar", textarea: true, rows: 2 },
    ],
  },
  {
    eyebrow: "Reel",
    fields: [
      { key: "reel_youtube_id", label: "YouTube video ID", placeholder: "YwIJ2pDzUuQ" },
      { key: "reel_intro", label: "Reel intro paragraph", textarea: true, rows: 3 },
    ],
  },
  {
    eyebrow: "Pulse Portal",
    fields: [
      { key: "portal_overview", label: "Pulse Portal overview", textarea: true, rows: 4 },
    ],
  },
  {
    eyebrow: "Casting Portal",
    fields: [
      { key: "casting_portal", label: "Casting Portal description", textarea: true, rows: 4 },
    ],
  },
  {
    eyebrow: "Record Label",
    fields: [
      { key: "label_reach", label: "Label reach paragraph", textarea: true, rows: 4 },
      { key: "partnerships", label: "Partnerships paragraph", textarea: true, rows: 3 },
    ],
  },
  {
    eyebrow: "Closing",
    fields: [
      { key: "closing_headline", label: "Closing headline", placeholder: "Let's build something the culture remembers." },
      { key: "closing_body", label: "Closing body", textarea: true, rows: 3 },
      { key: "contact_email", label: "Contact email" },
      { key: "contact_url", label: "Contact URL", placeholder: "pulsemedia.vip" },
    ],
  },
];

export default function PitchDeckAdmin() {
  const [config, setConfig] = useState(null);
  const [shows, setShows] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [openShow, setOpenShow] = useState(false);
  const [s, setS] = useState(EMPTY_SHOW);
  const [savingShow, setSavingShow] = useState(false);
  const [uploading, setUploading] = useState(false);
  const posterRef = useRef(null);

  /* Share links + analytics */
  const [shares, setShares] = useState([]);
  const [newShareName, setNewShareName] = useState("");
  const [creatingShare, setCreatingShare] = useState(false);

  const load = async () => {
    const [cfgRes, showsRes, sharesRes] = await Promise.all([
      api.get("/admin/pitch-deck/config"),
      api.get("/admin/pitch-deck/shows"),
      api.get("/admin/pitch-deck/shares"),
    ]);
    setConfig(cfgRes.data);
    setShows(showsRes.data);
    setShares(sharesRes.data);
  };
  useEffect(() => { load(); }, []);

  const setField = (key, val) => setConfig((c) => ({ ...c, [key]: val }));

  const saveConfig = async () => {
    if (!config) return;
    setSavingCfg(true);
    try {
      const payload = { ...config };
      delete payload.id;
      const r = await api.put("/admin/pitch-deck/config", payload);
      setConfig(r.data);
      toast.success("Pitch deck content saved");
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingCfg(false);
    }
  };

  const generatePdf = async () => {
    setGenerating(true);
    try {
      const r = await api.get("/admin/pitch-deck/pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PulseMedia_PitchDeck_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Pitch deck downloaded");
    } catch (e) {
      toast.error("Generation failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  /* ---- Show editor ---- */
  const openNewShow = () => { setS(EMPTY_SHOW); setOpenShow(true); };
  const openEditShow = (sh) => {
    setS({ id: sh.id, title: sh.title || "", tagline: sh.tagline || "", synopsis: sh.synopsis || "", extended_synopsis: sh.extended_synopsis || "", poster: sh.poster || "", link: sh.link || "", trailer_url: sh.trailer_url || "", genre: sh.genre || "", runtime: sh.runtime || "", director: sh.director || "", year: sh.year || "", starring: sh.starring || "" });
    setOpenShow(true);
  };

  const uploadPoster = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", files[0]);
      const r = await api.post("/admin/pitch-deck/shows/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data.urls?.[0];
      if (url) setS((p) => ({ ...p, poster: url }));
      toast.success("Poster uploaded");
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const saveShow = async (e) => {
    e.preventDefault();
    if (!s.title.trim()) { toast.error("Title is required"); return; }
    setSavingShow(true);
    try {
      const payload = { title: s.title, tagline: s.tagline, synopsis: s.synopsis, extended_synopsis: s.extended_synopsis, poster: s.poster, link: s.link, trailer_url: s.trailer_url, genre: s.genre, runtime: s.runtime, director: s.director, year: s.year, starring: s.starring };
      if (s.id) {
        await api.put(`/admin/pitch-deck/shows/${s.id}`, payload);
        toast.success("Show updated");
      } else {
        await api.post("/admin/pitch-deck/shows", payload);
        toast.success("Show added");
      }
      setOpenShow(false); setS(EMPTY_SHOW); load();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSavingShow(false);
    }
  };

  const delShow = async (sh) => {
    if (!window.confirm(`Delete "${sh.title}"?`)) return;
    try { await api.delete(`/admin/pitch-deck/shows/${sh.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  /* ---- Drag-to-reorder shows ---- */
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);
  const onDragStart = (e, id) => { dragId.current = id; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e, id) => { e.preventDefault(); if (id !== overId) setOverId(id); };
  const onDragEnd = () => { dragId.current = null; setOverId(null); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const fromId = dragId.current; setOverId(null); dragId.current = null;
    if (!fromId || fromId === targetId) return;
    const next = [...shows];
    const fi = next.findIndex((r) => r.id === fromId);
    const ti = next.findIndex((r) => r.id === targetId);
    if (fi === -1 || ti === -1) return;
    const [m] = next.splice(fi, 1);
    next.splice(ti, 0, m);
    setShows(next);
    try { await api.post("/admin/pitch-deck/shows/reorder", { ids: next.map((r) => r.id) }); }
    catch { toast.error("Reorder failed — reverting"); load(); }
  };

  /* ---- Share link handlers ---- */
  const createShare = async () => {
    if (!newShareName.trim()) { toast.error("Give the share a name first (e.g. 'Empire Music')"); return; }
    setCreatingShare(true);
    try {
      const r = await api.post("/admin/pitch-deck/shares", { name: newShareName.trim() });
      setNewShareName(""); load();
      const publicUrl = `${window.location.origin}/deck/${r.data.token}`;
      try { await navigator.clipboard.writeText(publicUrl); toast.success("Share link created and copied to clipboard"); }
      catch { toast.success("Share link created"); }
    } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
    finally { setCreatingShare(false); }
  };
  const copyShareUrl = async (token) => {
    const url = `${window.location.origin}/deck/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — long-press to copy: " + url); }
  };
  const revokeShare = async (share) => {
    if (!window.confirm(`Revoke "${share.name}"? This link will stop working immediately.`)) return;
    try { await api.delete(`/admin/pitch-deck/shares/${share.id}`); toast.success("Revoked"); load(); }
    catch { toast.error("Revoke failed"); }
  };
  const fmtDate = (iso) => {
    if (!iso) return "never";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  if (!config) {
    return <div className="text-zinc-500 py-20 text-center" data-testid="pitch-deck-loading">Loading pitch deck…</div>;
  }

  return (
    <div data-testid="admin-pitch-deck">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="label-mono mb-2">/ Marketing</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Pitch Deck</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
            Edit the company copy + Pulse Portal show pages, then export a fully branded PDF
            that pulls in every artist on the roster. The deck regenerates fresh every download.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} className="glass-btn ghost" data-testid="pitch-deck-reload">
            <RefreshCw size={14}/> Reload
          </button>
          <button onClick={saveConfig} disabled={savingCfg} className="glass-btn" data-testid="pitch-deck-save">
            <Save size={14}/> {savingCfg ? "Saving…" : "Save copy"}
          </button>
          <button onClick={generatePdf} disabled={generating} className="glass-btn solid" data-testid="pitch-deck-generate">
            <Download size={14}/> {generating ? "Generating…" : "Generate PDF"}
          </button>
        </div>
      </div>

      {/* Config form */}
      <div className="space-y-6">
        {FIELD_GROUPS.map((group) => (
          <section
            key={group.eyebrow}
            className="rounded-2xl border border-white/5 bg-[#121215] p-6"
            data-testid={`pitch-deck-section-${group.eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="label-mono text-cyan-400 mb-4">/ {group.eyebrow}</div>
            <div className="grid md:grid-cols-2 gap-4">
              {group.fields.map((f) => (
                <div
                  key={f.key}
                  className={f.textarea && f.rows > 2 ? "md:col-span-2" : ""}
                >
                  <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">{f.label}</label>
                  {f.textarea ? (
                    <textarea
                      value={config[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      rows={f.rows || 3}
                      placeholder={f.placeholder || ""}
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none resize-y"
                      data-testid={`pitch-deck-field-${f.key}`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={config[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      placeholder={f.placeholder || ""}
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
                      data-testid={`pitch-deck-field-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Shows */}
      <section className="mt-10 rounded-2xl border border-white/5 bg-[#121215] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label-mono text-cyan-400 mb-2">/ Pulse Portal Shows</div>
            <div className="text-zinc-500 text-xs">{shows.length} show{shows.length === 1 ? "" : "s"} · drag to reorder · displayed in this order in the deck</div>
          </div>
          <button onClick={openNewShow} className="glass-btn" data-testid="pitch-deck-show-new">
            <Plus size={14}/> Add show
          </button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shows.map((sh) => (
            <div
              key={sh.id}
              draggable
              onDragStart={(e) => onDragStart(e, sh.id)}
              onDragOver={(e) => onDragOver(e, sh.id)}
              onDrop={(e) => onDrop(e, sh.id)}
              onDragEnd={onDragEnd}
              className={`relative rounded-xl border overflow-hidden bg-[#0a0a0c] group ${overId === sh.id ? "border-cyan-400/60 ring-2 ring-cyan-400/30" : "border-white/5"}`}
              data-testid={`pitch-deck-show-${sh.id}`}
            >
              <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-400 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" title="Drag to reorder">
                <GripVertical size={12}/>
              </div>
              <div className="aspect-[3/4] bg-black/40 overflow-hidden">
                {sh.poster ? (
                  <img src={`${API_URL.replace(/\/api$/, "")}${sh.poster}`} alt={sh.title} className="w-full h-full object-cover pointer-events-none" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">No poster</div>
                )}
              </div>
              <div className="p-4">
                <div className="font-display text-lg font-bold tracking-tight" data-testid={`pitch-deck-show-title-${sh.id}`}>{sh.title}</div>
                {sh.tagline && <div className="text-cyan-400 text-xs italic mt-0.5 line-clamp-1">{sh.tagline}</div>}
                {sh.synopsis && <div className="text-zinc-400 text-xs mt-2 line-clamp-3 leading-relaxed">{sh.synopsis}</div>}
                <div className="flex items-center gap-2 mt-2 text-[10px] flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded ${sh.extended_synopsis ? "bg-cyan-400/20 text-cyan-300" : "bg-white/5 text-zinc-500"}`}>
                    {sh.extended_synopsis ? "+ extended" : "no extended"}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded ${sh.link ? "bg-cyan-400/20 text-cyan-300" : "bg-white/5 text-zinc-500"}`}>
                    {sh.link ? "linked" : "no link"}
                  </span>
                  {sh.metadata_doc_id && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/20" title="Title, synopsis, and poster are auto-synced from a Metadata Document. Edits to those fields will be overwritten on next metadata save. Tagline, extended description, and link stay yours.">
                      ↻ synced
                    </span>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEditShow(sh)} className="glass-btn ghost py-1 px-2 text-xs" data-testid={`pitch-deck-show-edit-${sh.id}`}>
                    <Edit2 size={12}/> Edit
                  </button>
                  <button onClick={() => delShow(sh)} className="glass-btn ghost py-1 px-2 text-xs text-red-400" data-testid={`pitch-deck-show-del-${sh.id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Share & Analytics */}
      <section className="mt-10 rounded-2xl border border-white/5 bg-[#121215] p-6" data-testid="deck-shares-section">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <div className="label-mono text-cyan-400 mb-2">/ Online Viewer · Share & Analytics</div>
            <div className="text-zinc-500 text-xs">
              Generate a unique link that opens the deck in a beautiful scrollable web view —
              perfect for sending to partners without attaching a 12 MB PDF.
            </div>
          </div>
        </div>

        {/* Create new */}
        <div className="flex gap-2 mb-5 flex-wrap">
          <input
            type="text" value={newShareName}
            onChange={(e) => setNewShareName(e.target.value)}
            placeholder="Name (e.g. Empire Music, Sundance Programmers)"
            className="flex-1 min-w-[200px] bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
            data-testid="deck-share-new-name"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createShare(); } }}
          />
          <button onClick={createShare} disabled={creatingShare} className="glass-btn solid text-sm" data-testid="deck-share-create">
            <LinkIcon size={14}/> {creatingShare ? "Creating…" : "Create share link"}
          </button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {shares.length === 0 && (
            <div className="text-zinc-500 text-sm text-center py-6">
              No share links yet — name your first one above to get a public URL.
            </div>
          )}
          {shares.map((sh) => {
            const url = `${window.location.origin}/deck/${sh.token}`;
            return (
              <div key={sh.id} className="rounded-lg border border-white/5 bg-black/30 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`deck-share-${sh.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base font-semibold">{sh.name}</div>
                  <div className="font-mono text-[11px] text-zinc-500 truncate" title={url}>{url}</div>
                  <div className="text-[10px] text-zinc-600 mt-1 flex gap-4 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-cyan-400">
                      <Users size={10}/> {sh.view_count} view{sh.view_count === 1 ? "" : "s"}
                    </span>
                    <span>last viewed: {fmtDate(sh.last_viewed_at)}</span>
                    <span>created: {fmtDate(sh.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <button onClick={() => copyShareUrl(sh.token)} className="glass-btn ghost text-xs py-1.5 px-2.5" data-testid={`deck-share-copy-${sh.id}`}>
                    <Copy size={12}/> Copy
                  </button>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="glass-btn ghost text-xs py-1.5 px-2.5" data-testid={`deck-share-open-${sh.id}`}>
                    <Eye size={12}/> Preview
                  </a>
                  <button onClick={() => revokeShare(sh)} className="glass-btn ghost text-xs py-1.5 px-2.5 text-red-400" data-testid={`deck-share-revoke-${sh.id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Show modal */}
      <Modal open={openShow} onClose={() => setOpenShow(false)} title={s.id ? "Edit show" : "New show"}>
        <form onSubmit={saveShow} className="space-y-4" data-testid="pitch-deck-show-form">
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">Title</label>
            <input
              type="text" value={s.title}
              onChange={(e) => setS({ ...s, title: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="pitch-deck-show-input-title"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">Tagline (one line)</label>
            <input
              type="text" value={s.tagline}
              onChange={(e) => setS({ ...s, tagline: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="pitch-deck-show-input-tagline"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">Synopsis (1 short paragraph)</label>
            <textarea
              value={s.synopsis} rows={4}
              onChange={(e) => setS({ ...s, synopsis: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-y"
              data-testid="pitch-deck-show-input-synopsis"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">Extended description (optional — fills the show page)</label>
            <textarea
              value={s.extended_synopsis} rows={6}
              onChange={(e) => setS({ ...s, extended_synopsis: e.target.value })}
              placeholder="Longer detail that prints under the main synopsis. Use a blank line to break paragraphs."
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-y"
              data-testid="pitch-deck-show-input-extended"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">Watch link (Portal URL — makes the poster + button clickable)</label>
            <input
              type="text" value={s.link}
              onChange={(e) => setS({ ...s, link: e.target.value })}
              placeholder="https://pulseportal.tv/inner-smile"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="pitch-deck-show-input-link"
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">Trailer link (YouTube or Vimeo URL — shows a "Watch Trailer" button in the online deck)</label>
            <input
              type="text" value={s.trailer_url}
              onChange={(e) => setS({ ...s, trailer_url: e.target.value })}
              placeholder="https://youtu.be/..."
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="pitch-deck-show-input-trailer"
            />
          </div>

          <div className="border-t border-white/10 pt-4">
            <div className="text-xs text-cyan-400 mb-3 uppercase tracking-wider font-bold">Project Details (shown under the synopsis)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Genre</label>
                <input type="text" value={s.genre} onChange={(e) => setS({ ...s, genre: e.target.value })} placeholder="Horror / Thriller"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="pitch-deck-show-input-genre"/>
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Runtime</label>
                <input type="text" value={s.runtime} onChange={(e) => setS({ ...s, runtime: e.target.value })} placeholder="1h 42m"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="pitch-deck-show-input-runtime"/>
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Year</label>
                <input type="text" value={s.year} onChange={(e) => setS({ ...s, year: e.target.value })} placeholder="2026"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="pitch-deck-show-input-year"/>
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Director</label>
                <input type="text" value={s.director} onChange={(e) => setS({ ...s, director: e.target.value })} placeholder="Nicholas Carr (KeeZY)"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="pitch-deck-show-input-director"/>
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Starring (comma-separated)</label>
                <input type="text" value={s.starring} onChange={(e) => setS({ ...s, starring: e.target.value })} placeholder="KeeZY, Mariah Chavis, Royel Bridges"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="pitch-deck-show-input-starring"/>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 uppercase tracking-wider">Poster (3:4 portrait — auto-resized to 1400px)</label>
            <div className="flex items-center gap-3">
              <div className="w-24 h-32 rounded-lg overflow-hidden bg-black/40 border border-white/10 flex-shrink-0">
                {s.poster ? (
                  <img src={`${API_URL.replace(/\/api$/, "")}${s.poster}`} alt="poster" className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">No poster</div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={posterRef} type="file" accept="image/*" onChange={(e) => uploadPoster(e.target.files)} className="hidden"/>
                <button type="button" onClick={() => posterRef.current?.click()} className="glass-btn ghost text-sm" data-testid="pitch-deck-show-upload">
                  <Upload size={14}/> {uploading ? "Uploading…" : "Upload poster"}
                </button>
                <input
                  type="text" value={s.poster}
                  onChange={(e) => setS({ ...s, poster: e.target.value })}
                  placeholder="/api/uploads/deck/shows/…"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-400"
                  data-testid="pitch-deck-show-input-poster"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpenShow(false)} className="glass-btn ghost text-sm">Cancel</button>
            <button type="submit" disabled={savingShow} className="glass-btn solid text-sm" data-testid="pitch-deck-show-save">
              <FileText size={14}/> {savingShow ? "Saving…" : "Save show"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
